import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  Crown,
  Lock,
  Shield,
  Sparkles,
  Eye,
  EyeOff,
  FileLock2,
  Hash,
  Layers,
} from "lucide-react";

/**
 * FoundingDoctrinePanel — V23.15.24 (Doctrine 50 · doctrine fondatrice opaque).
 *
 * Affiche à toutes les orgs clientes (et à l'Ultra Admin) :
 *  - Texte de présentation générique (jamais le contenu réel)
 *  - Toggle "Activer le moteur de raisonnement stratégique propriétaire"
 *
 * Pour l'Ultra Admin uniquement (org founder), expose en plus :
 *  - Section "Appropriation sélective — Corpus anonymisé" qui liste les
 *    principes personnels de toutes les orgs clientes (anonymisés via hash).
 *  - Action "Marquer cette doctrine comme fondatrice" (depuis la liste des
 *    doctrines existantes de l'Ultra Admin).
 *
 * Garde-fou éthique : le composant n'expose JAMAIS le contenu de la doctrine
 * fondatrice aux orgs non-founder. Seul le statut + texte générique transitent.
 */
export default function FoundingDoctrinePanel({ doctrines, onMarkedFoundational }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showCorpus, setShowCorpus] = useState(false);

  const loadStatus = async () => {
    try {
      const r = await api.get("/workspace/doctrines/founding/status");
      setStatus(r.data);
    } catch {
      // Endpoint indispo (rare) → on cache silencieusement le panneau
      setStatus({ unavailable: true });
    }
  };
  useEffect(() => { loadStatus(); }, []);

  const toggle = async (next) => {
    setBusy(true);
    try {
      const r = await api.post("/workspace/doctrines/founding/toggle", { enabled: next });
      notify.success(next ? "Moteur fondateur activé." : "Moteur fondateur désactivé.");
      setStatus((s) => ({
        ...(s || {}),
        enabled: r.data?.effective_enabled,
      }));
    } catch (e) {
      notify.error(fmtError(e, "Toggle failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!status || status.unavailable) return null;

  const isFounder = status.is_founder_org === true;
  const available = status.available === true;
  const enabled = status.enabled === true;

  return (
    <div className="space-y-4" data-testid="founding-doctrine-panel">
      <div className="noam-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="flex items-center gap-2">
              <Crown className="w-4 h-4 text-[#EF9F27]" />
              <div className="noam-overline">Doctrine 50 · Moteur stratégique propriétaire</div>
            </div>
            <h2 className="text-base font-medium text-zinc-100 mt-1.5">
              {isFounder
                ? "Vous êtes le détenteur de la doctrine fondatrice"
                : "Activer le moteur de raisonnement stratégique propriétaire"}
            </h2>
            <p className="text-xs text-zinc-400 mt-2 max-w-2xl leading-relaxed">
              {status.presentation_text}
            </p>
            {!available && (
              <div className="mt-3 text-[11px] text-zinc-500 flex items-center gap-1.5">
                <Lock className="w-3 h-3" />
                Aucun moteur propriétaire configuré pour l&apos;instant.
              </div>
            )}
            {isFounder && (
              <div className="mt-3 text-[11px] text-[#EF9F27] flex items-start gap-1.5">
                <Shield className="w-3 h-3 mt-0.5 flex-shrink-0" />
                Votre doctrine active alimente le moteur que les organisations clientes peuvent activer.
                Son contenu n&apos;est jamais exposé aux autres orgs.
              </div>
            )}
          </div>
          {available && !isFounder && (
            <FoundingToggle
              active={enabled}
              disabled={busy}
              onChange={() => toggle(!enabled)}
            />
          )}
        </div>

        {/* Section Ultra Admin : marquer une doctrine comme fondatrice */}
        {isFounder && Array.isArray(doctrines) && doctrines.length > 0 && (
          <MarkFoundationalSection
            doctrines={doctrines}
            onMarked={async () => {
              await loadStatus();
              if (onMarkedFoundational) onMarkedFoundational();
            }}
          />
        )}
      </div>

      {/* Corpus anonymisé pour Ultra Admin uniquement */}
      {isFounder && (
        <div className="noam-card p-5" data-testid="founding-doctrine-corpus-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-[#378ADD]" />
              <div className="noam-overline">Doctrine 50 · Appropriation sélective</div>
            </div>
            <button
              type="button"
              onClick={() => setShowCorpus((v) => !v)}
              className="text-xs px-2.5 py-1.5 rounded-md border border-white/10 hover:bg-white/5 inline-flex items-center gap-1.5"
              data-testid="founding-doctrine-corpus-toggle"
            >
              {showCorpus ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {showCorpus ? "Masquer le corpus" : "Voir le corpus anonymisé"}
            </button>
          </div>
          <p className="text-xs text-zinc-400 mt-2 max-w-2xl leading-relaxed">
            Corpus des principes personnels rédigés par les organisations clientes, présenté de
            manière anonymisée (org_id → hash, aucun nom ni email). Les principes les plus récurrents
            apparaissent en premier — signal pertinent pour identifier ce qui mérite d&apos;être
            généralisé puis reformulé pour la doctrine fondatrice v3.
          </p>
          {showCorpus && <AnonymizedCorpusList />}
        </div>
      )}
    </div>
  );
}

function FoundingToggle({ active, disabled, onChange }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange?.(!active)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
        active ? "bg-[#EF9F27]" : "bg-zinc-700"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      data-testid="founding-doctrine-toggle"
      aria-pressed={active}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          active ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

function MarkFoundationalSection({ doctrines, onMarked }) {
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState(false);
  const handleMark = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await api.post("/ultra-admin/doctrines/mark-foundational", { doctrine_id: selectedId });
      notify.success("Doctrine marquée comme fondatrice.");
      if (onMarked) await onMarked();
    } catch (e) {
      notify.error(fmtError(e, "Échec du marquage"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 pt-4 border-t border-white/5 space-y-2">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
        <FileLock2 className="w-3 h-3" />
        Marquer une de vos doctrines comme fondatrice (Ultra Admin)
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          className="text-xs bg-zinc-900 border border-white/10 rounded-md px-2 py-1.5 text-zinc-200 min-w-[280px]"
          data-testid="founding-doctrine-mark-select"
        >
          <option value="" className="bg-zinc-900 text-zinc-100">— Choisir une doctrine —</option>
          {doctrines.map((d) => (
            <option key={d.id} value={d.id} className="bg-zinc-900 text-zinc-100">
              {d.title}{d.is_foundational ? " (★ fondatrice)" : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleMark}
          disabled={!selectedId || busy}
          className="text-xs px-3 py-1.5 rounded-md bg-[#EF9F27]/15 hover:bg-[#EF9F27]/25 text-[#EF9F27] disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="founding-doctrine-mark-btn"
        >
          {busy ? "..." : "Marquer fondatrice"}
        </button>
      </div>
    </div>
  );
}

function AnonymizedCorpusList() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/ultra-admin/doctrines/anonymized-corpus");
        setData(r.data);
      } catch (e) {
        notify.error(fmtError(e, "Corpus indisponible"));
        setData({ items: [], total: 0 });
      }
    })();
  }, []);

  if (!data) {
    return <div className="text-xs text-zinc-500 mt-3">Chargement…</div>;
  }
  const items = data.items || [];
  if (items.length === 0) {
    return (
      <div className="text-xs text-zinc-500 mt-3" data-testid="founding-corpus-empty">
        Aucun principe personnel n&apos;a encore été rédigé par les organisations clientes.
      </div>
    );
  }
  return (
    <div className="mt-4 space-y-1.5 max-h-[500px] overflow-y-auto" data-testid="founding-corpus-list">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 sticky top-0 bg-zinc-950/95 backdrop-blur py-1">
        {items.length} principe(s) anonymisé(s) — récurrence ↓
      </div>
      {items.slice(0, 100).map((it, idx) => (
        <div
          key={`${it.principle_hash}-${idx}`}
          className="px-3 py-2 rounded-md bg-white/[0.02] border border-white/5 text-[11px]"
          data-testid={`founding-corpus-row-${it.section}`}
        >
          <div className="flex items-center gap-2 mb-1">
            <span
              className="text-[9px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded"
              style={{
                backgroundColor:
                  it.section === "principles" ? "#7F77DD15" :
                  it.section === "filters" ? "#378ADD15" : "#EF9F2715",
                color:
                  it.section === "principles" ? "#7F77DD" :
                  it.section === "filters" ? "#378ADD" : "#EF9F27",
              }}
            >
              {it.section}
            </span>
            {it.recurrence_count > 1 && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">
                ×{it.recurrence_count}
              </span>
            )}
            <span className="text-[9px] text-zinc-600 flex items-center gap-1 ml-auto">
              <Hash className="w-2.5 h-2.5" />
              {(it.anon_org_hash || "").slice(0, 8)}
            </span>
          </div>
          <div className="text-zinc-200 leading-relaxed">{it.text}</div>
        </div>
      ))}
      {items.length > 100 && (
        <div className="text-[10px] text-zinc-500 text-center py-2">
          + {items.length - 100} autres principes (limité à 100 affichés)
        </div>
      )}
    </div>
  );
}
